import React from "react";
import "../tttAcademy/TTTAcademy.css";
import { Card, CardContainer } from "../../../components/Card";
import useSWR from "swr";
import API_ENDPOINTS from "../../../apis/endpoints";
import { BarChart, LineChart } from "../../../components/Charts";
import {
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
} from "@mui/material";
import TopCards from "./TopCards";
import LastRefreshed from "../LastRefreshed";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export default function Under25() {
    const {
        data: weekDataSet,
        error: weekError,
        isLoading: weekIsLoading,
    } = useSWR(`${API_ENDPOINTS.U25_WEEKS}`);

    const { data: weekData, refreshed_at } = weekDataSet ?? {};

    if (weekIsLoading) {
        return <CircularProgress />;
    }

    return <WeekDisplay weeks={weekData} refreshed_at={refreshed_at} />;
}

function WeekDisplay({ weeks, refreshed_at }) {
    const [selectedWeeks, setSelectedWeeks] = React.useState(
        weeks.slice(-6).map((week) => week.key)
    );

    const handleWeekChange = (event) => {
        setSelectedWeeks(event.target.value);
    };
    return (
        <Card>
            <div className="header-section">
                <Typography variant="highlightSemibold">Under25</Typography>
                <FormControl sx={{ m: 1, width: 300 }}>
                    <InputLabel id="week-multi-select-label">Weeks</InputLabel>
                    <Select
                        labelId="week-multi-select-label"
                        id="week-multi-select"
                        multiple
                        value={selectedWeeks}
                        onChange={handleWeekChange}
                        input={<OutlinedInput label="All Weeks" />}
                        renderValue={(selected) =>
                            weeks.length === selectedWeeks.length
                                ? "All weeks"
                                : weeks
                                      ?.filter((week) =>
                                          selected.includes(week.key)
                                      )
                                      .map((week) => week.name)
                                      .join(", ")
                        }
                        MenuProps={MenuProps}
                    >
                        {weeks?.map((week) => (
                            <MenuItem key={week.key} value={week.key}>
                                <Checkbox
                                    checked={
                                        selectedWeeks.indexOf(week.key) > -1
                                    }
                                />
                                <ListItemText primary={week.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <SelectedWeeks weeks={selectedWeeks} />
            <LastRefreshed date={refreshed_at} />
        </Card>
    );
}

function SelectedWeeks({ weeks }) {
    const { data: data, isLoading: isLoading } = useSWR(
        `${API_ENDPOINTS.U25_DATA}?weeks=${weeks.join(",")}`
    );

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <div className="cards-container">
            <CardContainer columns={2}>
                <TopCards data={data} />

                <BarChart
                    data={data}
                    series={[
                        {
                            name: "Transactions",
                            key: "transactions",
                        },
                        {
                            name: "Student Wallet",
                            key: "student_wallet",
                        },
                    ]}
                    xKey="week"
                    title="Student Economy"
                    yOverlap={[
                        {
                            name: "Transactions",
                        },
                        {
                            name: "Student Wallet",
                            opposite: true,
                        },
                    ]}
                />
            </CardContainer>
            <CardContainer columns={2}>
                <LineChart
                    data={data}
                    series={[
                        {
                            name: "Missions",
                            key: "missions",
                        },
                    ]}
                    xKey="week"
                    title="Student Economy (Missions)"
                />
                <BarChart
                    data={data}
                    series={[
                        {
                            name: "Student Earners",
                            key: "student_earners",
                        },
                        {
                            name: "Unique Earners",
                            key: "unique_earners",
                        },
                    ]}
                    xKey="week"
                    title="Student Earners vs Unique Earners"
                    yOverlap={[
                        {
                            name: "Student Earners",
                        },
                        {
                            name: "Unique Earners",
                            opposite: true,
                        },
                    ]}
                />
            </CardContainer>
            <CardContainer columns={2}>
                <BarChart
                    data={data}
                    series={[
                        {
                            name: "Instagram Views",
                            key: "instagram_views",
                        },
                        {
                            name: "Instagram Reach",
                            key: "instagram_reach",
                        },
                        {
                            name: "Instagram Followers",
                            key: "instagram_followers_cumulative",
                        },
                    ]}
                    xKey="week"
                    title="Instagram Reach vs Views vs Followers Stats"
                />
                <LineChart
                    data={data}
                    series={[
                        {
                            name: "Downloads (Cumulative)",
                            key: "downloads_cumulative",
                        },
                    ]}
                    xKey="week"
                    title="App Downloads"
                />
            </CardContainer>
            <CardContainer columns={2}>
                <LineChart
                    data={data}
                    series={[
                        {
                            name: "Instagram Shares",
                            key: "instagram_shares",
                        },
                    ]}
                    xKey="week"
                    // yLabel="Seats Sold"
                    title="Instagram Shares Stats"
                />
                <LineChart
                    data={data}
                    series={[
                        {
                            name: "Dau",
                            key: "dau",
                        },
                        {
                            name: "Stickiness",
                            key: "stickiness",
                        },
                    ]}
                    xKey="week"
                    title="Product Metrics"
                />
            </CardContainer>
            <CardContainer columns={2}>
                <LineChart
                    data={data}
                    series={[
                        {
                            name: "CEO Program Applications",
                            key: "ceo_program_applications",
                        },
                        {
                            name: "Campus CEO’s",
                            key: "campus_ceos",
                        },
                        {
                            name: "Campus Clubs",
                            key: "campus_clubs",
                        },
                        {
                            name: "Club Page",
                            key: "club_page",
                        },
                    ]}
                    xKey="week"
                    title="Student Programs"
                />
            </CardContainer>
        </div>
    );
}
